import {
  AdminBrandTemplatePickerModal,
  AdminLocationAndBrandPickerModal,
  AdminLocationPickerModal,
  AdminSelect,
  AdvancedQueryTypes,
  Div,
  H3,
  handleSetUrlSearchParams,
  isAdmin,
  isPartner,
  isToday,
  P,
  renderAdvancedQueryTypesOptionLabel,
  setTimeTo,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { SetURLSearchParams } from 'react-router-dom';

export const SummaryFilter = ({
  searchParams,
  setSearchParams,
  advancedQueryTypes,
}: {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  advancedQueryTypes: AdvancedQueryTypes[];
}) => {
  const today = new Date();
  const [selectLocation, setSelectLocation] =
    useState<AdvancedQueryTypes | null>(null);
  const [selectLocationAndBrand, setSelectLocationAndBrand] =
    useState<AdvancedQueryTypes | null>(null);
  const [selectBrandTemplate, setSelectBrandTemplate] =
    useState<AdvancedQueryTypes | null>(null);

  const date = [setTimeTo(today, 'start'), setTimeTo(today, 'end')] as const;

  const dateFrom = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date[0]);

  const dateTo = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date[1]);

  const renderDateLabel = () => {
    if (isToday(date[0]) && isToday(date[1])) {
      return 'Today';
    } else return `${dateFrom} - ${dateTo}`;
  };

  const renderDateLabelToday = () => {
    if (isToday(date[0]) && isToday(date[1])) {
      return <P $mb="0">{dateFrom}</P>;
    } else return null;
  };

  const advancedQueryType = searchParams.get('type') ?? advancedQueryTypes[0];
  const locationId = searchParams.get('locationId');

  const setAdvancedQueryType = (value: string | null) => {
    handleSetUrlSearchParams('type', value, setSearchParams);
  };

  const requiredLocationOnly =
    advancedQueryType ===
      AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND ||
    advancedQueryType ===
      AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS ||
    advancedQueryType === AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS ||
    advancedQueryType === AdvancedQueryTypes.LOCATION_SPECIFIC_ONLY_V_GRUBS;

  useEffect(() => {
    if (requiredLocationOnly && !locationId) {
      setSelectLocation(advancedQueryType);
    }
  }, [locationId]);

  const filterTypeOptions = (
    <>
      {advancedQueryTypes.map((r, i) => {
        return (
          <option value={r} key={i}>
            {renderAdvancedQueryTypesOptionLabel(r as AdvancedQueryTypes)}
          </option>
        );
      })}
    </>
  );

  return (
    <Div $m="32px 0" $mSM="20px 0" $flex $column $alignEnd $width="100%">
      <Div $between $flex $alignCenter $gap="32px" $gapSM="16px" $width="100%">
        <Div $width="35%" $alignEnd $gap="5px">
          <H3
            $mb="0"
            style={{
              whiteSpace: 'nowrap',
            }}
          >
            {renderDateLabel()} {renderDateLabelToday()}
          </H3>
        </Div>

        <Div $width="100%" $flexSM $maxWidth="400px" $column $flex $alignEnd>
          <AdminSelect
            value={advancedQueryType}
            options={filterTypeOptions}
            onChange={({ target }) => {
              if (
                target.value ===
                  AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND &&
                !locationId
              ) {
                setSelectLocation(
                  AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND
                );
                return;
              }
              if (
                target.value ===
                  AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS &&
                !locationId
              ) {
                setSelectLocation(
                  AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS
                );
                return;
              }
              if (
                target.value ===
                  AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS &&
                !locationId
              ) {
                setSelectLocation(
                  AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS
                );
                return;
              }
              if (
                target.value ===
                  AdvancedQueryTypes.LOCATION_SPECIFIC_ONLY_V_GRUBS &&
                !locationId
              ) {
                setSelectLocation(
                  AdvancedQueryTypes.LOCATION_SPECIFIC_ONLY_V_GRUBS
                );
                return;
              }
              if (target.value === AdvancedQueryTypes.V_GRUBS_BRAND_SPECIFIC) {
                setSelectBrandTemplate(
                  AdvancedQueryTypes.V_GRUBS_BRAND_SPECIFIC
                );
                return;
              }
              if (target.value === AdvancedQueryTypes.LOCATION_BRAND_SPECIFIC) {
                setSelectLocationAndBrand(
                  AdvancedQueryTypes.LOCATION_BRAND_SPECIFIC
                );
                return;
              }

              setAdvancedQueryType(target.value);
            }}
            $shadowDesign
          />
        </Div>
      </Div>

      {(isAdmin || isPartner) && (
        <>
          {' '}
          <AdminLocationPickerModal
            selectedLocation={null}
            isOpen={!!selectLocation}
            onClose={() => {
              setSelectLocation(null);
            }}
            changeParams={() => {
              setSelectLocation(advancedQueryType as AdvancedQueryTypes);
            }}
            filterActive={
              advancedQueryType ===
                AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND ||
              advancedQueryType ===
                AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS ||
              advancedQueryType ===
                AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS ||
              advancedQueryType ===
                AdvancedQueryTypes.LOCATION_SPECIFIC_ONLY_V_GRUBS
            }
            setSelectedLocation={() => undefined}
            handleSubmit={(location) => {
              setAdvancedQueryType(selectLocation);
              handleSetUrlSearchParams(
                'locationId',
                location.id,
                setSearchParams
              );
              handleSetUrlSearchParams(
                'locationName',
                location.name,
                setSearchParams
              );
              setSelectLocation(null);
            }}
          />
          <AdminLocationAndBrandPickerModal
            isOpen={!!selectLocationAndBrand}
            onClose={() => {
              setSelectLocationAndBrand(null);
            }}
            changeParams={() => {
              setSelectLocationAndBrand(
                advancedQueryType as AdvancedQueryTypes
              );
            }}
            filterActive={
              advancedQueryType === AdvancedQueryTypes.LOCATION_BRAND_SPECIFIC
            }
            handleSubmit={(location, brand) => {
              setAdvancedQueryType(selectLocationAndBrand);
              handleSetUrlSearchParams(
                'locationId',
                location.id,
                setSearchParams
              );
              handleSetUrlSearchParams(
                'locationName',
                location.name,
                setSearchParams
              );
              handleSetUrlSearchParams('brandId', brand.id, setSearchParams);
              handleSetUrlSearchParams(
                'brandName',
                brand.name,
                setSearchParams
              );
              setSelectLocationAndBrand(null);
            }}
          />
          <AdminBrandTemplatePickerModal
            selectedLocation={null}
            isOpen={!!selectBrandTemplate}
            filterActive={
              advancedQueryType === AdvancedQueryTypes.V_GRUBS_BRAND_SPECIFIC
            }
            onClose={() => {
              setSelectBrandTemplate(null);
            }}
            changeParams={() => {
              setSelectBrandTemplate(advancedQueryType as AdvancedQueryTypes);
            }}
            setSelectedLocation={() => undefined}
            handleSubmit={(brandTemplate) => {
              setAdvancedQueryType(selectBrandTemplate);
              handleSetUrlSearchParams(
                'brandTemplateId',
                brandTemplate.id,
                setSearchParams
              );
              handleSetUrlSearchParams(
                'brandTemplateName',
                brandTemplate.name,
                setSearchParams
              );
              setSelectBrandTemplate(null);
            }}
          />
        </>
      )}
    </Div>
  );
};
